import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const Gastown = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighborhoodNameWithCity = `${neighborhoodName}, ${cityName}`
  const neighbourhoodWithCityAndState = `${neighborhoodName}, ${cityNameWithState}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.gastown-vancouver:About Gastown Paragraph">
          Gastown is Vancouver’s oldest neighborhood, known for its historic
          charm and vibrant culture. Established in 1867 by John &quot;Gassy
          Jack&quot; Deighton, a talkative Englishman who opened the area’s
          first saloon, Gastown has evolved from a single tavern into a bustling
          district. Its cobblestone streets, Victorian architecture, and
          antique-style lamp posts create a unique and picturesque atmosphere.
          Today, Gastown seamlessly blends the old with the new, offering a mix
          of trendy boutiques, diverse eateries, art galleries, and a lively
          nightlife scene.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment or room to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in Neighbourhood Paragraph">
            Finding {{ filterNameWithFallback }} in{' '}
            {{ neighborhoodNameWithCity }} requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e8%b5%81%e6%b3%95/%e5%8a%a0%e6%8b%bf%e5%a4%a74%e7%9c%81%e7%a7%9f%e6%88%bf%e6%8a%bc%e9%87%91%e3%80%81%e7%bb%ad%e7%ba%a6%e3%80%81%e7%a7%9f%e9%87%91%e6%b6%a8%e5%b9%85%e3%80%81%e6%94%af%e4%bb%98%e6%9d%a1%e6%ac%be%e6%af%94/">
              如何在加拿大租房？
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/">
              租$1000可以在加拿大租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada?
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodNameWithCity}
        cityNameWithState={neighbourhoodWithCityAndState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$1,564 - $1,964',
          bed2: '$3,347 - $3,747',
          bed3: '$3,857 - $4,257'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.gastown-vancouver:Apartment Hunting Gastown Paragraph 1">
            Finding an apartment for rent in Gastown, Vancouver, BC requires
            careful planning and consideration. Start by determining your
            criteria and having all necessary documents ready for your
            application. Once you find an ideal apartment, review the{' '}
            <LinkText href="https://liv.rent/blog/rental-laws/residential-tenancy-agreement-explained/">
              lease agreement
            </LinkText>{' '}
            thoroughly, paying attention to terms, payment schedule, and
            restrictions. Consider{' '}
            <LinkText href="https://liv.rent/blog/lifestyle/finding-a-roommate-during-covid19/">
              roommates
            </LinkText>{' '}
            or co-living options to split costs. Take advantage of building
            amenities but be aware that they may increase monthly rent.{' '}
            <LinkText href="https://liv.rent/blog/renters/checklist-prepare-to-move-house/">
              Plan your move in advance
            </LinkText>
            , booking necessary services and notifying utility companies.
            Finally, embrace the vibrant Gastown lifestyle by exploring your new
            neighborhood and connecting with your neighbors.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.gastown-vancouver:Apartment Hunting Gastown Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long-term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short-term rentals</LinkText>. To
            start your search, you can refer to our guides on{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/">
              neighbourhoods in BC
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.gastown-vancouver:Apartments for rent in Gastown Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Gastown, Vancouver
              </LinkText>
              , offer a mix of historic loft conversions and modern high-rise
              condos, making them ideal for young professionals, artists, and
              urban dwellers. Many units feature exposed brick walls, large
              windows, and industrial-style finishes. With its proximity to
              downtown and easy access to transit, renting an apartment in
              Gastown provides a stylish, convenient urban living experience.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.gastown-vancouver:Houses for rent in Gastown Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Gastown, Vancouver
              </LinkText>
              , are rare due to the neighborhood’s primarily commercial and
              residential high-rise landscape. However, a few heritage homes and
              townhouses can be found in the surrounding areas. These properties
              offer unique architectural charm, spacious layouts, and a chance
              to live in one of Vancouver’s most historic and vibrant
              communities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.gastown-vancouver:Townhouses for rent in Gastown Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Gastown, Vancouver
              </LinkText>{' '}
              , provide a unique living experience, blending heritage aesthetics
              with modern comfort. These multi-level homes often feature
              loft-style designs, exposed brick, and contemporary interiors.
              Ideal for professionals and families seeking a stylish, walkable
              neighborhood, Gastown townhouses offer easy access to dining,
              shopping, and entertainment.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.gastown-vancouver:Basements for rent in Gastown Paragraph">
              <LinkText href={links.basement}>
                Basements for rent in Gastown, Vancouver
              </LinkText>
              , are a rare find due to the neighborhood’s high-rise and
              commercial nature. However, some the neighborhood’s high-rise and
              commercial nature. However, some converted heritage buildings may
              offer lower-level suites, perfect for students or professionals
              looking for an affordable way to live in a historic, centrally
              located area.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.gastown-vancouver:Condos for rent Gastown Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Gastown, Vancouver
              </LinkText>
              , appeal to professionals and creatives seeking modern, stylish
              living spaces. Many condos feature loft-style designs, large
              windows, and views of the city or waterfront. With access to
              boutique shopping, top-rated restaurants, and transit, Gastown
              condos offer a premium urban lifestyle in a historic setting.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.gastown-vancouver:Rooms for rent in Gastown Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in Gastown, Vancouver
              </LinkText>{' '}
              , cater to students, artists, and professionals who want to
              experience city living on a budget. Many rentals are in shared
              lofts or apartments, providing access to the neighborhood’s
              vibrant culture and amenities while keeping costs affordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals NEIGHBOURHOOD Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodNameWithCity }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Try searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals in NEIGHBOURHOOD Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodNameWithCity }}. Renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.gastown-vancouver:Short term rentals in Gastown Paragraph">
              <LinkText href={links.shortTerm}>
                Short-term rentals in Gastown, Vancouver
              </LinkText>
              , are popular among tourists, business travelers, and digital
              nomads. The area offers stylish Airbnb lofts, boutique hotel
              stays, and furnished apartments with easy access to downtown
              attractions. Staying in Gastown provides a mix of history,
              nightlife, and urban excitement.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals in NEIGHBOURHOOD Generic Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighborhoodNameWithCity }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getExploring = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.gastown-vancouver:Exploring Gastown Paragraph">
          Gastown is one of Vancouver’s most exciting neighborhoods, where
          history meets contemporary culture.{' '}
          <LinkText href="https://www.atlasobscura.com/places/steam-clock-gastown-vancouver">
            The iconic Steam Clock
          </LinkText>
          , built in 1977, draws visitors with its whistling steam-powered
          chimes.{' '}
          <LinkText href="https://en.wikipedia.org/wiki/statue_of_john_deighton">
            The Gassy Jack Statue
          </LinkText>
          , honoring the neighborhood’s founder, is a must-see landmark. Gastown
          is also known for its vibrant nightlife, featuring cocktail lounges,
          speakeasies, and trendy bars. Art lovers can explore galleries
          showcasing local and Indigenous artists, while shoppers can browse
          unique fashion boutiques. With its stunning mix of historic
          architecture, waterfront views, and lively cultural scene, Gastown is
          a dynamic destination for locals and visitors alike.
        </Trans>
      </Paragraph>
    )
  }

  const getThingsToDo = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.gastown-vancouver:Things To Do Gastown Paragraph">
          Gastown offers plenty of attractions and activities for all interests.
          Stroll down Water Street to admire the Steam Clock and explore its
          many independent boutiques. Enjoy gourmet dining at{' '}
          <LinkText href="https://www.labattoir.ca/">L’Abattoir</LinkText>, a
          top-rated French restaurant, or sip handcrafted cocktails at{' '}
          <LinkText href="https://gastown.org/listing/the-diamond-restaurant/">
            The Diamond
          </LinkText>
          . Visit the{' '}
          <LinkText href="https://vancouverlookout.com/">
            Vancouver Lookout
          </LinkText>{' '}
          for panoramic views of the city or explore Vancouver’s past at the{' '}
          <LinkText href="https://museumofvancouver.ca/">
            Museum of Vancouver
          </LinkText>
          . Gastown’s historic charm, vibrant nightlife, and cultural offerings
          make it a top destination in Vancouver.
        </Trans>
      </Paragraph>
    )
  }

  const getRestaurants = () => {
    return (
      <Trans i18nKey="seo.hood.gastown-vancouver:Restaurants Gastown Paragraph">
        <Paragraph>
          Gastown Vancouver offers a range of dining options for every taste.
          Here are three restaurants worth trying.
        </Paragraph>
        <Paragraph>
          1) <LinkText href="https://www.labattoir.ca/">L’Abattoir</LinkText> -
          renowned for its French-inspired West Coast fare, blending classic
          techniques with local ingredients. Signature dishes include the
          pan-seared duck breast and the hearty beef bourguignon. The
          restaurant’s chic, industrial setting, housed in a former butchery,
          offers a unique dining ambiance. It’s an ideal spot for those seeking
          a sophisticated meal in a historic setting.
        </Paragraph>
        <Paragraph>
          2) <LinkText href="https://www.tacofino.com/">Tacofino</LinkText> -
          brings the flavors of Baja California to Gastown, serving up some of
          the city’s best fish tacos. The menu features a variety of
          Mexican-inspired dishes, with a focus on fresh, sustainable
          ingredients. The vibrant, casual atmosphere makes it a favorite among
          locals and visitors alike.
        </Paragraph>
        <Paragraph>
          3){' '}
          <LinkText href="https://www.pourhousevancouver.com/">
            Pourhouse
          </LinkText>{' '}
          - channels old-school charm with creative cocktails and a menu of
          comfort food classics. Diners can enjoy dishes like the signature
          Pourhouse Burger in a setting reminiscent of a 1920s speakeasy. It’s a
          go-to spot for cocktail enthusiasts and those seeking a cozy dining
          experience.
        </Paragraph>
        <Paragraph>
          4) <LinkText href="https://monarcavancouver.ca/">Monarca</LinkText> -
          offers a modern Mexican vision, showcasing dishes like grilled octopus
          and an extensive selection of mezcal cocktails. The contemporary
          setting and innovative menu make it a standout in Gastown’s dining
          scene. It’s a must-visit for those seeking a fresh take on traditional
          Mexican cuisine.
        </Paragraph>
      </Trans>
    )
  }

  const getTransportation = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.gastown-vancouver:Transportation Gastown Paragraph">
          Getting around Gastown, Vancouver, BC, is convenient with various
          transportation options. The city’s{' '}
          <LinkText href="https://www.translink.ca/">
            public transit system
          </LinkText>
          , including buses and the{' '}
          <LinkText href="https://www.translink.ca/schedules-and-maps/skytrain">
            SkyTrain
          </LinkText>
          , provides easy access to surrounding areas. For cyclists, Vancouver
          features an extensive network of bike lanes and rental services. Many
          parts of Gastown are also walkable, with key amenities and attractions
          nearby. Drivers will find a mix of street parking and parking garages
          available. No matter your preferred way of getting around, Gastown
          offers accessible and efficient transportation choices.
        </Trans>
      </Paragraph>
    )
  }

  const getWhereToStay = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.gastown-vancouver:Where To Stay Gastown Paragraph">
          Accommodation options in Gastown, Vancouver cater to those looking for
          a more local and immersive experience. This area offers a mix of
          boutique guesthouses, bed-and-breakfasts, and{' '}
          <LinkText href={links.shortTerm}>short-term rentals</LinkText>{' '}
          available through platforms like{' '}
          <LinkText href="https://www.airbnb.ca/">Airbnb</LinkText>, providing
          travelers with a unique stay. While large hotels may be limited within
          Gastown itself, its proximity to other parts of Vancouver ensures that
          a range of traditional accommodations, from luxury to budget-friendly,
          is just a short distance away. Staying in Gastown allows visitors to
          experience its distinct character while remaining conveniently close
          to the city’s main attractions.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodNameWithCity}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: getExploring(),
    thingsToDo: getThingsToDo(),
    restaurants: getRestaurants(),
    transportation: getTransportation(),
    whereToStay: getWhereToStay()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
    />
  )
}

export default Gastown
